import {ChipKind} from './aggregate';
import {AccessRequest, RequestStatus, TempAccessRequest} from './api';

/**
 * Unified row model for the admin History tab. Built on the frontend by
 * joining two parallel server-side streams:
 *
 *   - CloudWatch "Temp data access granted" / "access-ops.impersonation-granted"
 *     events (what actually happened in IAM / the access-ops-grants DDB).
 *   - DDB-backed AccessRequest rows (the request-side view, including denied
 *     and pending — neither of which produces a CW event).
 *
 * Each AccessRequest is fanned out into one row per (kind, shardId). Wildcard
 * blocks (`tenantIds: ['*']`) collapse into a single row with `shardId='*'`.
 * For each CW event we then try to find a matching AccessRequest row (same
 * kind + email + shardId or wildcard + decidedAt close to grantedAt). Matches
 * are dropped (the AccessRequest row already represents that grant); misses
 * are emitted as their own row with `status='synced'` and a `source`
 * classification:
 *
 *   - DA event with no match → 'iam-bastion-legacy' (legacy Slack
 *     `/access-temp` or a hand-added IAM DataAccess policy)
 *   - Imp event with no match → 'access-ops-impersonation' (an impersonation
 *     grant with no matching AccessRequest — e.g. a direct DDB grant)
 */

export type HistoryStatus = RequestStatus | 'synced';
export type SyncSource = 'iam-bastion-legacy' | 'access-ops-impersonation';

export interface HistoryRow {
    /** Stable key for React, unique within a render. */
    key: string;
    kind: ChipKind;
    /**
     * "When did this thing happen" — ISO 8601.
     * - CW-sourced row: the log event's `grantedAt`.
     * - AccessRequest-sourced row: `decidedAt` for decided rows, `createdAt`
     *   for pending. Used purely for sorting + the When column.
     */
    at: string;
    /** Email of the recipient. Always present for AccessRequest rows, may be
     *  resolved-from-username for CW DA rows (or undefined if the lookup misses). */
    email?: string;
    /** IAM username — only populated when the row came from a CW DA event. */
    username?: string;
    /** Single shard uid, or `'*'` for an All-tenants block. */
    shardId: string;
    reason: string;
    /** AccessRequest status, or `'synced'` for CW-only rows. */
    status: HistoryStatus;
    /** Set when `status='synced'` — see SyncSource. */
    source?: SyncSource;
    /** Admin who approved/denied (or requester for auto-approved). */
    decidedBy?: string;
    /** ISO 8601 — when the admin acted. */
    decidedAtIso?: string;
    /** AccessRequest id when we matched one. */
    requestId?: string;
    /** Original CW row if this row is CW-backed (or matched against one). */
    cwRequest?: TempAccessRequest;
}

/**
 * Time tolerance for matching CW DA events against an AccessRequest. Per-shard
 * temp-access DA invocations are synchronous Lambda calls — a few seconds — but
 * we keep a generous window to absorb clock skew and any retry latency.
 */
const DA_MATCH_TOLERANCE_MS = 20 * 60 * 1000;
/**
 * Impersonation events come from our own log group milliseconds after
 * `decidedAt`. 60s is generous and avoids clock-skew false negatives.
 */
const IMP_MATCH_TOLERANCE_MS = 60 * 1000;

export interface BuildHistoryRowsInput {
    cwRequests: TempAccessRequest[];
    accessRequests: AccessRequest[];
    /**
     * IAM username → email map, used to resolve CW DA events to a recipient
     * email so we can match against AccessRequest.requesterEmail. Built on
     * the page from the loaded grants. Missing entries are tolerated — a CW
     * DA event with an unknown username just won't match an AR by email and
     * will fall through to the `synced` bucket.
     */
    emailByUsername: ReadonlyMap<string, string>;
}

export function buildHistoryRows(input: BuildHistoryRowsInput): HistoryRow[] {
    const {cwRequests, accessRequests, emailByUsername} = input;
    const out: HistoryRow[] = [];

    // 1) Fan out each AccessRequest into per-shard rows. Each fan-out row
    //    carries the AR's status; matching CW events against it happens in
    //    step 2 and only flips a flag so we know not to double-emit.
    interface FanOutRow extends HistoryRow {
        /** Absorbs any CW event whose (kind, shardId, time) matches. */
        absorbedCw?: TempAccessRequest;
        /** Bag of acceptable shardIds — `['*']` for wildcard, else the shard list. */
        matchableShards: Set<string>;
        wildcard: boolean;
        decidedAtMs?: number;
    }

    const fanOut: FanOutRow[] = [];
    for (const ar of accessRequests) {
        const atMs = ar.decidedAt ?? ar.createdAt;
        const atIso = new Date(atMs).toISOString();
        const baseDecidedAtIso = ar.decidedAt ? new Date(ar.decidedAt).toISOString() : undefined;
        const baseDecidedAtMs = ar.decidedAt;

        if (ar.dataAccess) {
            const wildcard = ar.dataAccess.tenantIds.includes('*');
            if (wildcard) {
                fanOut.push({
                    key: `ar:${ar.id}:da:*`,
                    kind: 'data-access',
                    at: atIso,
                    email: ar.requesterEmail,
                    shardId: '*',
                    reason: ar.reason,
                    status: ar.status,
                    decidedBy: ar.decidedBy,
                    decidedAtIso: baseDecidedAtIso,
                    requestId: ar.id,
                    matchableShards: new Set(['*']),
                    wildcard: true,
                    decidedAtMs: baseDecidedAtMs,
                });
            } else {
                for (const shardId of ar.dataAccess.tenantIds) {
                    fanOut.push({
                        key: `ar:${ar.id}:da:${shardId}`,
                        kind: 'data-access',
                        at: atIso,
                        email: ar.requesterEmail,
                        shardId,
                        reason: ar.reason,
                        status: ar.status,
                        decidedBy: ar.decidedBy,
                        decidedAtIso: baseDecidedAtIso,
                        requestId: ar.id,
                        matchableShards: new Set([shardId]),
                        wildcard: false,
                        decidedAtMs: baseDecidedAtMs,
                    });
                }
            }
        }
        for (const {block, kind, tag} of [
            {block: ar.impersonation, kind: 'imp-decisions' as const, tag: 'imp'},
            {block: ar.impAi, kind: 'imp-ai' as const, tag: 'impai'},
        ]) {
            if (!block) {
                continue;
            }
            const wildcard = block.tenantIds.includes('*');
            const shardIds = wildcard ? ['*'] : block.tenantIds;
            for (const shardId of shardIds) {
                fanOut.push({
                    key: `ar:${ar.id}:${tag}:${shardId}`,
                    kind,
                    at: atIso,
                    email: ar.requesterEmail,
                    shardId,
                    reason: ar.reason,
                    status: ar.status,
                    decidedBy: ar.decidedBy,
                    decidedAtIso: baseDecidedAtIso,
                    requestId: ar.id,
                    matchableShards: new Set([shardId]),
                    wildcard,
                    decidedAtMs: baseDecidedAtMs,
                });
            }
        }
    }

    // 2) Walk CW events. For each one, try to find an AR fan-out row that
    //    "covers" it (same kind, same shard or wildcard, same email, decidedAt
    //    close to grantedAt). If found, attach the CW event to that AR row;
    //    if not, emit a `synced` row.
    for (const cw of cwRequests) {
        const cwEmail =
            cw.email ?? (cw.username ? emailByUsername.get(cw.username) : undefined);
        const cwAtMs = Date.parse(cw.grantedAt);
        const tolerance =
            cw.kind === 'data-access' ? DA_MATCH_TOLERANCE_MS : IMP_MATCH_TOLERANCE_MS;

        const match = fanOut.find(row => {
            if (row.kind !== cw.kind) {
                return false;
            }
            if (!row.matchableShards.has(cw.shardId) && !row.wildcard) {
                return false;
            }
            // Email match: be lenient when we couldn't resolve the CW email
            // (DA with unknown IAM username) — fall through to shard+time only
            // and accept the join. Otherwise require email parity.
            if (cwEmail && row.email && cwEmail !== row.email) {
                return false;
            }
            if (row.decidedAtMs === undefined) {
                // Pending AR — by definition no decidedAt, so no time anchor
                // exists. A pending AR shouldn't have a CW event yet.
                return false;
            }
            return Math.abs(cwAtMs - row.decidedAtMs) <= tolerance;
        });

        if (match) {
            // Attach the CW row for downstream consumers (RequestDetailDrawer
            // wants the policyTimestamp / grantedAt details).
            match.absorbedCw = cw;
            match.cwRequest = cw;
            continue;
        }

        // No matching AccessRequest → emit as a synced row.
        out.push({
            key: `cw:${cw.kind}:${cw.policyTimestamp}:${cw.username ?? cw.email ?? 'unknown'}:${cw.shardId}`,
            kind: cw.kind,
            at: cw.grantedAt,
            email: cwEmail,
            username: cw.username,
            shardId: cw.shardId,
            reason: cw.reason,
            status: 'synced',
            source:
                cw.kind === 'data-access' ? 'iam-bastion-legacy' : 'access-ops-impersonation',
            cwRequest: cw,
        });
    }

    // 3) Now collapse fan-out rows back into HistoryRow[]. Strip the matcher
    //    bookkeeping fields.
    for (const f of fanOut) {
        const {matchableShards: _ms, wildcard: _w, decidedAtMs: _d, absorbedCw: _a, ...row} = f;
        void _ms;
        void _w;
        void _d;
        void _a;
        out.push(row);
    }

    // 4) Sort newest-first by `at`.
    out.sort((a, b) => b.at.localeCompare(a.at));
    return out;
}

/**
 * Human-readable label for the sync source — used by the History table's
 * Status cell when `status === 'synced'`.
 */
export function syncSourceLabel(source: SyncSource | undefined): string {
    switch (source) {
        case 'iam-bastion-legacy':
            return 'IAM Bastion';
        case 'access-ops-impersonation':
            return 'AccessOps Impersonation';
        default:
            return 'unknown';
    }
}
